import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef
} from 'react';
import { lock, unlock } from 'tua-body-scroll-lock';
import ReactModal from 'react-modal';

import { noop } from '@/utils';

// reset default modal styles
ReactModal.defaultStyles.overlay = {};
ReactModal.defaultStyles.content = {};

ReactModal.setAppElement('#modal');

interface ModalProps extends ReactModal.Props {
  onAfterClose?: () => void;
  onAfterOpen?: () => void;
}

const ModalBase = forwardRef<HTMLElement, ModalProps>(({
  onAfterClose: onAfterCloseProps = noop,
  onAfterOpen: onAfterOpenProps = noop,
  ...props
}, ref) => {
  const modalRef = useRef<HTMLElement | null>(null);

   
  useImperativeHandle(ref, () => modalRef.current!, [modalRef]);

  const onAfterClose = useCallback(() => {
    if (modalRef.current) {
      unlock(modalRef.current);
    }

    onAfterCloseProps();
  }, [modalRef, onAfterCloseProps]);

  const onAfterOpen = useCallback(() => {
    if (!modalRef.current) {
      return;
    }

    /* -- ios hack -- */
    const storedRequestAnimationFrame = window.requestAnimationFrame;
    window.requestAnimationFrame = () => 42; // disable render
    /* -- ios hack -- */

    lock(modalRef.current);

    /* -- ios hack -- */
    window.requestAnimationFrame = storedRequestAnimationFrame; // enable render
    /* -- ios hack -- */

    onAfterOpenProps();
  }, [modalRef, onAfterOpenProps]);

  const setOverlayRef = useCallback((overlayRef: HTMLElement) => {
    // ref is reset before closing the modal
    if (overlayRef) {
      modalRef.current = overlayRef;
    }
  }, []);

  return (
    <ReactModal
      overlayRef={setOverlayRef}
      onAfterClose={onAfterClose}
      onAfterOpen={onAfterOpen}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
});

export default memo(ModalBase);
